import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

import ElementaryProgressReportDao from "../dao/ElementaryProgressReportDao";
import isGradingProfileWindowOpen from "../utils/ActionControls/isGradingProfileWindowOpen";

const useStudentData = ({ view, sharedGroup, studentNumber, flattened, selectedEpr, token, actionWindowsDto }) => {
    const [studentProfileData, setStudentProfileData] = useState(null);
    const [gradingProfileDto, setGradingProfileDto] = useState(null);
    const [isActiveWindow, setIsActiveWindow] = useState(false);

    // Resolve the student from either groups or homeroom
    const student = useMemo(() => {
        if (view === "GROUPS" && sharedGroup) {
            const s =
                sharedGroup.sharedGroupStudents?.find((x) => String(x.studentNumber) === String(studentNumber)) ?? null;
            if (!s) return null;
            return { ...s };
        }
        return flattened.find((s) => String(s.studentNumber) === String(studentNumber));
    }, [view, sharedGroup, studentNumber, flattened]);

    // Always mirrors the student currently on screen — used to ignore stale fetches.
    const displayedGpsIdRef = useRef(student?.gradingProfileStudentId ?? null);
    displayedGpsIdRef.current = student?.gradingProfileStudentId ?? null;
    const displayedStudentNumberRef = useRef(student?.studentNumber ?? null);
    displayedStudentNumberRef.current = student?.studentNumber ?? null;

    // Derived values
    const gradingProfileId = selectedEpr?.key ?? studentProfileData?.gradingProfileId;
    const gradingProfileStudentId = studentProfileData?.gradingProfileStudentId;

    // Clear profile immediately on student switch so overall-comment hydrate cannot
    // briefly apply the previous student's profile onto the new student.
    useEffect(() => {
        setStudentProfileData(null);
        setGradingProfileDto(null);
    }, [student?.gradingProfileStudentId]);

    // Fetch student profile data
    useEffect(() => {
        if (!student) return;
        const fetchForGpsId = student.gradingProfileStudentId;

        const options = {
            action: "getStudentProfileData",
            gradingProfileStudentId: fetchForGpsId,
            token
        };
        ElementaryProgressReportDao(options)
            .then((response) => {
                if (String(displayedGpsIdRef.current) !== String(fetchForGpsId)) return;
                if (response?.data?.payload) {
                    setStudentProfileData(response.data.payload);
                }
            })
            .catch((error) => {
                if (String(displayedGpsIdRef.current) !== String(fetchForGpsId)) return;
                toast.error("There was a problem loading student data.");
                console.error(error);
            });
    }, [student, token]);

    // Fetch student resources
    useEffect(() => {
        const fetchStudentResources = async () => {
            if (!gradingProfileId || !student?.studentNumber || !token || !gradingProfileStudentId) return;
            const fetchForGpsId = gradingProfileStudentId;
            const fetchForStudentNumber = student.studentNumber;

            try {
                const response = await ElementaryProgressReportDao({
                    action: "getStudentResources",
                    gradingProfileId,
                    params: {
                        studentNumbers: String(fetchForStudentNumber)
                    },
                    token
                });

                if (String(displayedGpsIdRef.current) !== String(fetchForGpsId)) return;
                if (String(displayedStudentNumberRef.current) !== String(fetchForStudentNumber)) return;

                const payload = response?.data?.payload || [];
                const dtoForStudent = payload.find(
                    (dto) => String(dto.studentNumber) === String(fetchForStudentNumber)
                );

                if (dtoForStudent) {
                    setStudentProfileData((prev) => ({
                        ...prev,
                        gradingProfileStudentId: dtoForStudent.key,
                        sectionId: dtoForStudent.sectionId,
                        externalResources: dtoForStudent.externalResources || []
                    }));
                }
            } catch (err) {
                if (String(displayedGpsIdRef.current) !== String(fetchForGpsId)) return;
                toast.error("Unable to retrieve student resources.");
                console.error("Error fetching student resources", err);
            }
        };

        fetchStudentResources();
    }, [gradingProfileStudentId, selectedEpr?.key, student?.studentNumber, gradingProfileId, token]);

    // Fetch grading profile DTO
    useEffect(() => {
        if (!token) return;
        if (studentProfileData && !gradingProfileDto) {
            const options = {
                action: "getGradingProfileById",
                gradingProfileId: studentProfileData.gradingProfileId,
                token
            };
            ElementaryProgressReportDao(options)
                .then((response) => {
                    const dto = response?.data?.payload;
                    setGradingProfileDto(dto);
                })
                .catch((error) => {
                    toast.error("There was a problem loading Report Card data.");
                    console.error(error);
                });
        }
    }, [gradingProfileDto, studentProfileData, token]);

    // Calculate active window state
    useEffect(() => {
        if (actionWindowsDto && gradingProfileDto) {
            const isWindowOpen = isGradingProfileWindowOpen(actionWindowsDto, gradingProfileDto.timePeriod);
            setIsActiveWindow(isWindowOpen);
        }
    }, [actionWindowsDto, gradingProfileDto]);

    return {
        student,
        studentProfileData,
        gradingProfileDto,
        isActiveWindow,
        gradingProfileId,
        gradingProfileStudentId,
        setStudentProfileData
    };
};

export default useStudentData;
